import { useEffect, useState, type CSSProperties } from "react";
import type { Game } from "@engine/core/Game";
import type { Inventory, ItemDB, ItemDef } from "@engine/gameplay";

interface Props {
  game?: Game | undefined;
  inventory?: Inventory | undefined;
  itemDb?: ItemDB | undefined;
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1400,
};

const panelStyle: CSSProperties = {
  width: 360,
  background: "#0f1218",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 8,
  color: "#e6e8ee",
  padding: 14,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 13,
  boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
};

const slotStyle: CSSProperties = {
  width: 56,
  height: 56,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 6,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  fontSize: 11,
  color: "#e6e8ee",
};

const rarityColor: Record<string, string> = {
  common: "#9ca3af",
  uncommon: "#4ade80",
  rare: "#60a5fa",
  epic: "#a855f7",
  legendary: "#facc15",
};

/**
 * Modal inventory window. Listens to the `inventory` action via `game.actions`
 * and toggles open/close. The grid renders the supplied `Inventory` slots.
 */
export function InventoryPanel({ game, inventory, itemDb }: Props) {
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!game) return;
    const onKey = (e: KeyboardEvent) => {
      // Don't trigger while typing in chat / rebind capture
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.code === "KeyI" || e.code === "Tab") {
        if (game.actions.bindingsFor("inventory").includes(e.code)) {
          e.preventDefault();
          setOpen((v) => !v);
        }
      } else if (e.code === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [game, open]);

  // Subscribe to inventory mutations so the grid re-renders.
  useEffect(() => {
    if (!inventory) return;
    const sub = (): void => setTick((t) => t + 1);
    const unsubs: Array<() => void> = [];
    unsubs.push(inventory.bus.on("added", sub));
    unsubs.push(inventory.bus.on("removed", sub));
    unsubs.push(inventory.bus.on("moved", sub));
    return () => {
      for (const u of unsubs) u();
    };
  }, [inventory]);

  if (!open) return null;

  return (
    <div style={overlayStyle} onMouseDown={() => setOpen(false)}>
      <div style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <strong>Inventory</strong>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#e6e8ee",
              padding: "2px 8px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            esc
          </button>
        </div>
        {!inventory && (
          <div style={{ opacity: 0.7 }}>
            No inventory bound to this player. Inject one via:
            <pre style={{ fontSize: 11, marginTop: 6 }}>
{`<InventoryPanel
  game={engine.game}
  inventory={player.inventory}
  itemDb={itemDb}
/>`}
            </pre>
          </div>
        )}
        {inventory && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 56px)",
              gap: 6,
              justifyContent: "center",
            }}
          >
            {inventory.slots.map((slot, i) => (
              <SlotCell key={i} slot={slot} itemDb={itemDb} />
            ))}
          </div>
        )}
        <div style={{ opacity: 0.55, marginTop: 12, fontSize: 11 }}>
          press <strong>I</strong> or <strong>Tab</strong> to close · Esc closes
        </div>
      </div>
    </div>
  );
}

function SlotCell({ slot, itemDb }: { slot: { itemId: number; count: number } | null; itemDb?: ItemDB | undefined }) {
  if (!slot) return <div style={slotStyle} />;
  const def: ItemDef | undefined = itemDb?.get(slot.itemId);
  const borderColor = def ? rarityColor[def.rarity] ?? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.18)";
  return (
    <div
      style={{
        ...slotStyle,
        border: `1px solid ${borderColor}`,
      }}
      title={def?.name ?? `item ${slot.itemId}`}
    >
      <span style={{ fontSize: 14, fontWeight: 600 }}>{def?.name?.slice(0, 4) ?? `#${slot.itemId}`}</span>
      {slot.count > 1 && <span style={{ opacity: 0.65 }}>×{slot.count}</span>}
    </div>
  );
}
